/**
 * Returns the input value unchanged. Useful as a default function or for functional programming patterns.
 */
export function identity<T>(a: T): T {
  return a;
}

/**
 * Returns the last element of an array. Throws if array is empty.
 */
export function last<T>(a: T[]): T {
  return a[a.length - 1];
}

/**
 * Composes multiple functions from right to left.
 */
export function compose<T>(...fns: Array<(arg: T) => T>): (arg: T) => T {
  return (arg: T) => fns.reduceRight((acc, fn) => fn(acc), arg);
}

/**
 * Generates an array of numbers within a specified inclusive range.
 *
 * @param start - The starting number of the range (inclusive).
 * @param end - The ending number of the range (inclusive).
 * @returns An array containing all numbers from `start` to `end`, inclusive.
 * @throws {Error} If `start` is greater than `end`.
 *
 * @example
 * ```typescript
 * range(1, 5); // returns [1, 2, 3, 4, 5]
 * ```
 */
export function range(start: number, end: number): number[] {
  if (start > end) {
    throw new Error("Start must be less than or equal to end");
  }
  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
}
